import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import './microbar.scss';
import { AlertTypes, InsightsSubMenu, LevelName, MainPages, PolicyMode, SavingStatus } from '@src/utils/enums';
import { ExportAllRecordsAlert } from './alerts/export-all-records-alert';
import {
    BackPolicyAlert,
    DeactivatedVersionAlert,
    DraftExistAlert,
    ErrorAlert,
    MaintenanceAlert,
    MicrobarAlert, ScheduledVersionAlert,
    SmartAlert
} from './alerts/microbar-alert';
import { useStores } from '../../store';

// Helper functions outside the component
const isDeactivatedVersion = (fwVersionStore: any) => fwVersionStore.isVersionDeactivate && !fwVersionStore.pendingVersion;
const isCustomProtection = (fwVersionStore: any, customerStore: any) => customerStore.selectedLocation.serviceLevel !== LevelName.BRONZE && fwVersionStore.fwVersions &&
    fwVersionStore.isInitialVersion && !fwVersionStore.pendingVersion && customerStore.areAllOtherServicesPolicyModeUndefined;
const isSmartProtection = (customerStore: any) => customerStore.isSmartProtectionRunning;
const isErrorStatus = (fwVersionStore: any) => fwVersionStore.savingStatus === SavingStatus.Error;
const isPullProtection = (customerStore: any, fwVersionStore: any) => customerStore.policyMode === PolicyMode.PULL_POLICY &&
    (fwVersionStore.isOneVersionActive || fwVersionStore.isSecondVersionInDraft);
const isDraftExist = (fwVersionStore: any) => fwVersionStore.selectedFwVersion && !!fwVersionStore.draftVersion &&
    fwVersionStore.selectedFwVersion.externalId !== fwVersionStore.draftVersion.externalId;
const isExportAllRecords = (logsStore: any) => logsStore.activeSubMenu === InsightsSubMenu.LOGS && logsStore.logsByType.length === logsStore.maxLogsForLocalDownload;

const getAlertConditions = (userStore: any, customerStore: any, fwVersionStore: any, logsStore: any) => [
    { condition: () => userStore.isReadOnlyUser, type: AlertTypes.READ_ONLY_USER },
    { condition: () => !customerStore.isAvailable, type: AlertTypes.MAINTENANCE },
    { condition: () => customerStore.isScheduledVersionBarVisible, type: AlertTypes.SCHEDULED_VERSION_COME_ON },
    { condition: () => isDeactivatedVersion(fwVersionStore), type: AlertTypes.DEACTIVATED_VERSION },
    { condition: () => isCustomProtection(fwVersionStore, customerStore), type: AlertTypes.CUSTOM_PROTECTION },
    { condition: () => isSmartProtection(customerStore), type: AlertTypes.SMART_PROTECTION },
    { condition: () => isErrorStatus(fwVersionStore), type: AlertTypes.ERROR },
    { condition: () => isPullProtection(customerStore, fwVersionStore), type: AlertTypes.PULL_PROTECTION },
    { condition: () => isDraftExist(fwVersionStore), type: AlertTypes.DRAFT_EXIST },
    { condition: () => isExportAllRecords(logsStore), type: AlertTypes.EXPORT_ALL_RECORDS }
];

export const MicroBar: React.FC = observer(() => {
    const { customerStore, fwVersionStore, logsStore, userStore, uiStore } = useStores();

    const pageAlertPriorities: { [key: string]: AlertTypes[] } = {
        [MainPages.INSIGHTS.toString()]:
            [AlertTypes.EXPORT_ALL_RECORDS,
                AlertTypes.SMART_PROTECTION,
                AlertTypes.READ_ONLY_USER,
                AlertTypes.MAINTENANCE,
                AlertTypes.SCHEDULED_VERSION_COME_ON,
                AlertTypes.DEACTIVATED_VERSION,
                AlertTypes.ERROR,
                AlertTypes.PULL_PROTECTION],
        [MainPages.POLICY.toString()]:
            [AlertTypes.SMART_PROTECTION,
                AlertTypes.READ_ONLY_USER,
                AlertTypes.MAINTENANCE,
                AlertTypes.SCHEDULED_VERSION_COME_ON,
                AlertTypes.DEACTIVATED_VERSION,
                AlertTypes.ERROR,
                AlertTypes.CUSTOM_PROTECTION,
                AlertTypes.DRAFT_EXIST,
                AlertTypes.PULL_PROTECTION],
        [MainPages.OVERVIEW.toString()]:
            [AlertTypes.SMART_PROTECTION,
                AlertTypes.READ_ONLY_USER,
                AlertTypes.MAINTENANCE,
                AlertTypes.SCHEDULED_VERSION_COME_ON,
                AlertTypes.DEACTIVATED_VERSION,
                AlertTypes.ERROR,
                AlertTypes.CUSTOM_PROTECTION,
                AlertTypes.PULL_PROTECTION],
        [InsightsSubMenu.REPORTS]:
            [AlertTypes.READ_ONLY_USER, AlertTypes.MAINTENANCE, AlertTypes.SCHEDULED_VERSION_COME_ON,AlertTypes.DEACTIVATED_VERSION,
                AlertTypes.ERROR, AlertTypes.CUSTOM_PROTECTION, AlertTypes.PULL_PROTECTION],
        'Other': [AlertTypes.READ_ONLY_USER, AlertTypes.MAINTENANCE, AlertTypes.DEACTIVATED_VERSION, AlertTypes.CUSTOM_PROTECTION, AlertTypes.PULL_PROTECTION]
    };

    const possibleAlertTypes = useMemo(() => {
        return getAlertConditions(userStore, customerStore, fwVersionStore, logsStore)
            .filter(({ condition }) => condition())
            .map(({ type }) => type);
    }, [userStore.isReadOnlyUser, logsStore.logsByType.length, fwVersionStore.draftVersion, logsStore.activeSubMenu, fwVersionStore.isInitialVersion, fwVersionStore.pendingVersion,
    fwVersionStore.selectedFwVersion, fwVersionStore.savingStatus, customerStore.isAvailable, customerStore.isPolicyModeFirstTime, customerStore.isScheduledVersionBarVisible,
    customerStore.selectedLocation, customerStore.isSmartProtectionRunning]);

    const alertType = useMemo(() => {
        let currentPageName;
        if (uiStore.mainPage === MainPages.INSIGHTS && logsStore.activeSubMenu === InsightsSubMenu.REPORTS) {
            currentPageName = InsightsSubMenu.REPORTS.toString();
        }
        else {
            currentPageName = uiStore.mainPage?.toString();
        }

        if (!currentPageName) return undefined;

        const priorities = pageAlertPriorities[currentPageName] || pageAlertPriorities['Other'];
        return priorities.find(type => possibleAlertTypes.includes(type));
    }, [possibleAlertTypes, uiStore.mainPage, logsStore.activeSubMenu]);

    const getAlertMessage = useMemo(() => {
        switch (alertType) {
            case AlertTypes.PULL_PROTECTION:
                return <BackPolicyAlert />;
            case AlertTypes.SMART_PROTECTION:
                return <SmartAlert />;
            case AlertTypes.CUSTOM_PROTECTION:
                return <BackPolicyAlert />;
            case AlertTypes.READ_ONLY_USER:
                return <MicrobarAlert />;
            case AlertTypes.MAINTENANCE:
                return <MaintenanceAlert />;
            case AlertTypes.DEACTIVATED_VERSION:
                return <DeactivatedVersionAlert />;
            case AlertTypes.DRAFT_EXIST:
                return <DraftExistAlert pathToDraftVersion={fwVersionStore.pathToDraftVersion} />;
            case AlertTypes.ERROR:
                return uiStore.mainPage === MainPages.POLICY ? <ErrorAlert /> : undefined;
            case AlertTypes.SCHEDULED_VERSION_COME_ON:
                return customerStore.sidMetadata ? <ScheduledVersionAlert /> : undefined;
            case AlertTypes.EXPORT_ALL_RECORDS:
                return uiStore.mainPage === MainPages.INSIGHTS ? 
                    <ExportAllRecordsAlert logType={logsStore.selectedLogsType} activeVersion={fwVersionStore.activeVersionData} /> : undefined;
            default:
                return undefined;
        }
    }, [uiStore.mainPage, alertType, customerStore.sidMetadata, fwVersionStore.pathToDraftVersion, logsStore.selectedLogsType, fwVersionStore.activeVersionData]);

    return <div className={`alert-container ${getAlertMessage === undefined ? 'unvisible' : ''}`}>
        <span className="alert-message">{getAlertMessage}</span>
    </div>;
});
